import { useEffect, useState } from "react";
import { Phone, Zap } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/scroll";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/constants";
import { Logo } from "@/components/Logo";

const NAV = [
    { id: "#how-it-works", key: "nav.how", testid: "how-it-works-nav-link" },
    { id: "#service-areas", key: "nav.areas", testid: "service-areas-nav-link" },
    { id: "#faq", key: "nav.faq", testid: "faq-nav-link" },
];

export const Header = () => {
    const { t, lang, toggle, isEs } = useLang();
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 border-b ${
                scrolled
                    ? "bg-obsidian/85 backdrop-blur-xl border-white/10 shadow-[0_8px_40px_rgba(0,0,0,0.45)]"
                    : "bg-obsidian/60 backdrop-blur-md border-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-3">
                <Logo />

                <nav className="hidden lg:flex items-center gap-1">
                    {NAV.map((n) => (
                        <a
                            key={n.id}
                            href={n.id}
                            data-testid={n.testid}
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToId(n.id);
                            }}
                            className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-300 hover:text-volt hover:bg-white/5 transition-colors"
                        >
                            {t(n.key)}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3">
                    <a
                        href={PHONE_TEL}
                        data-testid="nav-call-btn"
                        className="hidden sm:flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-bold text-white transition-all hover:border-volt/50 hover:bg-volt/10 hover:text-volt"
                    >
                        <Phone className="h-4 w-4 text-volt" />
                        <span>{PHONE_DISPLAY}</span>
                    </a>

                    <button
                        type="button"
                        data-testid="lang-toggle-btn"
                        onClick={toggle}
                        aria-label="Switch language"
                        className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-xs font-extrabold uppercase tracking-wider text-slate-300 transition-all hover:border-volt/50 hover:text-volt"
                    >
                        <Zap className="h-3.5 w-3.5 text-volt" />
                        <span className={isEs ? "text-volt" : ""}>EN</span>
                        <span className="text-slate-600">/</span>
                        <span className={!isEs ? "text-volt" : ""}>ES</span>
                    </button>

                    <button
                        type="button"
                        data-testid="nav-offer-btn"
                        onClick={() => scrollToId("#get-offer")}
                        className="relative hidden md:flex items-center gap-2 rounded-xl bg-volt px-4 py-2.5 text-sm font-extrabold text-obsidian transition-all hover:bg-volt-dark active:scale-[0.98]"
                    >
                        <span className="absolute inset-0 rounded-xl bg-volt animate-ping-slow -z-10" />
                        {t("nav.offer")}
                    </button>
                </div>
            </div>
        </header>
    );
};

export default Header;
